import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/templates/AuthLayout';
import { Button } from '../components/atoms/Button/Button';
import { Input } from '../components/atoms/Input/Input';
import { apiClient } from '../lib/apiClient';
import { confirmForgotPassword } from '../lib/cognitoClient';
import { useTranslation } from '../i18n/useTranslation';
import { getEmailErrorKey, getNewPasswordErrorKey } from '../lib/authValidation';
import './ResetPasswordPage.css';
import { Alert } from '../components/atoms/Alert/Alert';

interface ResetFormValues {
  email: string;
}

interface ConfirmFormValues {
  code: string;
  newPassword: string;
  confirmNewPassword: string;
}

interface FormErrors {
  email?: string;
  code?: string;
  newPassword?: string;
  confirmNewPassword?: string;
  general?: string;
}

type PageState = 'form' | 'confirm' | 'success';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [pageState, setPageState] = useState<PageState>('form');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const [resetValues, setResetValues] = useState<ResetFormValues>({ email: '' });
  const [confirmValues, setConfirmValues] = useState<ConfirmFormValues>({
    code: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  function validateEmail(vals: ResetFormValues): FormErrors {
    const errs: FormErrors = {};
    const emailError = getEmailErrorKey(vals.email);
    if (emailError) errs.email = t(emailError);
    return errs;
  }

  function validateConfirm(vals: ConfirmFormValues): FormErrors {
    const errs: FormErrors = {};
    if (!vals.code.trim()) {
      errs.code = t('auth.resetCodeRequired');
    }
    // Same Cognito policy the backend enforces for the new password.
    const passwordError = vals.newPassword ? getNewPasswordErrorKey(vals.newPassword) : 'auth.newPasswordRequired';
    if (passwordError) errs.newPassword = t(passwordError);
    if (!vals.confirmNewPassword) {
      errs.confirmNewPassword = t('auth.confirmNewPasswordRequired');
    } else if (vals.newPassword !== vals.confirmNewPassword) {
      errs.confirmNewPassword = t('auth.passwordsMustMatch');
    }
    return errs;
  }

  function handleResetChange(field: keyof ResetFormValues): (e: ChangeEvent<HTMLInputElement>) => void {
    return (e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      setResetValues(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        // eslint-disable-next-line security/detect-object-injection
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };
  }

  function handleConfirmChange(field: keyof ConfirmFormValues): (e: ChangeEvent<HTMLInputElement>) => void {
    return (e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      setConfirmValues(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        // eslint-disable-next-line security/detect-object-injection
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };
  }

  async function handleResetSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validateEmail(resetValues);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      const email = resetValues.email.trim().toLowerCase();
      await apiClient.post('/api/auth/forgot-password', { email });
      setSubmittedEmail(email);
      setPageState('confirm');
    } catch {
      setErrors({ general: t('auth.resetPasswordError') });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirmSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validateConfirm(confirmValues);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      await confirmForgotPassword(submittedEmail, confirmValues.code.trim(), confirmValues.newPassword);
      setPageState('success');
    } catch {
      setErrors({ general: t('auth.confirmResetError') });
    } finally {
      setIsLoading(false);
    }
  }

  if (pageState === 'success') {
    return (
      <AuthLayout
        title={t('auth.passwordChangedTitle')}
        subtitle={t('auth.passwordChangedSubtitle')}
      >
        <div className="reset-password__success">
          <Alert tone="success">{t('auth.passwordChangedMessage')}</Alert>
          <Link to="/login" className="reset-password__back-link">
            {t('auth.backToLogin')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (pageState === 'confirm') {
    return (
      <AuthLayout
        title={t('auth.confirmResetTitle')}
        subtitle={t('auth.confirmResetSubtitle')}
      >
        <form
          className="reset-password__form"
          onSubmit={(e) => { void handleConfirmSubmit(e); }}
          noValidate
          aria-label={t('auth.confirmResetFormLabel')}
        >
          {errors.general && (
            <Alert>{errors.general}</Alert>
          )}

          <div className="reset-password__field">
            <Input
              id="reset-code"
              label={t('auth.resetCode')}
              type="text"
              name="code"
              autoComplete="one-time-code"
              value={confirmValues.code}
              onChange={handleConfirmChange('code')}
              className="reset-password__input"
              error={errors.code}
              disabled={isLoading}
            />
          </div>

          <div className="reset-password__field">
            <Input
              id="reset-new-password"
              label={t('auth.newPassword')}
              type="password"
              name="newPassword"
              autoComplete="new-password"
              value={confirmValues.newPassword}
              onChange={handleConfirmChange('newPassword')}
              className="reset-password__input"
              error={errors.newPassword}
              disabled={isLoading}
            />
          </div>

          <div className="reset-password__field">
            <Input
              id="reset-confirm-password"
              label={t('auth.confirmNewPassword')}
              type="password"
              name="confirmNewPassword"
              autoComplete="new-password"
              value={confirmValues.confirmNewPassword}
              onChange={handleConfirmChange('confirmNewPassword')}
              className="reset-password__input"
              error={errors.confirmNewPassword}
              disabled={isLoading}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? t('auth.confirmingPassword') : t('auth.confirmPasswordBtn')}
          </Button>

          <div className="reset-password__footer">
            <Link to="/login" className="reset-password__login-link">
              {t('auth.backToLogin')}
            </Link>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.resetPasswordTitle')}
      subtitle={t('auth.resetPasswordSubtitle')}
    >
      <form
        className="reset-password__form"
        onSubmit={(e) => { void handleResetSubmit(e); }}
        noValidate
        aria-label={t('auth.resetPasswordFormLabel')}
      >
        {errors.general && (
          <Alert>{errors.general}</Alert>
        )}

        <div className="reset-password__field">
          <Input
            id="reset-email"
            label={t('auth.emailLabel')}
            type="email"
            name="email"
            autoComplete="email"
            value={resetValues.email}
            onChange={handleResetChange('email')}
            className="reset-password__input"
            error={errors.email}
            disabled={isLoading}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
        </Button>

        <div className="reset-password__footer">
          <Link to="/login" className="reset-password__login-link">
            {t('auth.backToLogin')}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export default ResetPasswordPage;
